import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import {
  AlertsContext,
  DistaContent,
  DistaPageHeaderProps,
} from "@sista/dista-ui";
import { SistaPageLayout } from "@sista/fe-commons";
import { IdmClientContext, Profile, ProfileType } from "@sista/idm-client";
import {
  Center,
  SistaDialog,
  UiContext,
  useActiveMenuItem,
  useSafeSistaAsync,
} from "@sista/library-stasi";
import { nanoid } from "nanoid";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useIdmClient } from "../../api/utils/useIdmClient";
import { useRejstrikClient } from "../../api/utils/useRejstrikClient";
import { useWorkflowClient } from "../../api/utils/useWorkflowClient";
import { useCanManageRepresentations } from "../../hooks/useCanManageRepresentations";
import { useLocalNavigation } from "../../hooks/useLocalNavigation";
import {
  formatRepresentationPeriod,
  pendingRepresentationInfo,
} from "../../utils/representation";
import { useProfileNames } from "../../utils/useProfileNames";
import { ProfileCard } from "../ui/ProfileCard";

export const ProfilesSettingsScreen = (props: {
  registerOrganization: boolean;
}) => {
  const rej = useRejstrikClient();
  const { loggedAccount, switchProfile, refresh } =
    useContext(IdmClientContext);
  const { confirm, openDialog } = useContext(UiContext);
  const { showSuccess } = useContext(AlertsContext);
  const { t } = useTranslation("ucet");
  const profileName = useProfileNames();
  const idm = useIdmClient();

  const doSwitch = (profile: Profile) => {
    switchProfile(profile.id);
  };

  useActiveMenuItem("settings-personal");

  useEffect(() => {
    if (props.registerOrganization == true) {
      openDialog("registerOrganization");
    }
  }, [openDialog, props.registerOrganization]);

  const [createProfileCall, createProfileState] = useSafeSistaAsync(
    idm.createLoggedAccountProfile
  );

  const doCreateProfile = useCallback(
    async (pt: ProfileType) => {
      const yes = await confirm({
        question: t(
          "profile.createConfirmation",
          "Chcete opravdu vytvořit nový profil?"
        ),
      });
      if (yes) {
        const result = await createProfileCall({ value: pt });
        if (typeof result === "object" && result) {
          showSuccess(t("ucet.profile.created", "Profil byl vytvořen"));
          refresh();
        }
      }
    },
    [confirm, createProfileCall, refresh, t, showSuccess]
  );

  // Zastup (dovolena apod.) si nastavuje zamestnanec poskytovatele sam za svuj PROVIDER profil
  // workflow provider_substitution - zastupce za cizi profil to nesmi (workflow ho stejne odmitne).
  const wf = useWorkflowClient();
  const navigate = useNavigate();
  const [startProcessCall] = useSafeSistaAsync(wf.startProcess);
  const [substitutionStarting, setSubstitutionStarting] = useState(false);
  const canSetSubstitute =
    loggedAccount.activeProfile?.type === "PROVIDER" &&
    loggedAccount.activeProfileRepresented !== true &&
    loggedAccount.providerId != null;

  const doSetSubstitute = useCallback(async () => {
    const context = [
      "urn:sista:providers:" + loggedAccount.providerId,
      "urn:sista:profiles:" + loggedAccount.activeProfileId,
    ];
    const processId = "provider_substitution-" + nanoid(12);
    setSubstitutionStarting(true);
    try {
      const result = await startProcessCall({
        id: processId,
        workflowId: "provider_substitution",
        context,
      });
      if (typeof result === "object" && result) {
        // ukol se na backendu zaklada asynchronne - pockame, at uz na strance ukolu je
        await new Promise((resolve) => setTimeout(resolve, 5000));
        // ukol k vyberu zastupce se zobrazi na obecne strance ukolu (public.taskUiUrl workflow)
        navigate(
          "/ukol/" + [...context, "urn:sista:processes:" + processId].join(",")
        );
      }
    } finally {
      setSubstitutionStarting(false);
    }
  }, [
    loggedAccount.providerId,
    loggedAccount.activeProfileId,
    startProcessCall,
    navigate,
  ]);

  // probihajici nebo budouci zastup, zatim nejvys jeden - zastupovany ho muze predcasne ukoncit;
  // kdyz zastup jeste nevznikl, rozpracovana zadost (bezici workflow) - lze ji otevrit nebo zrusit
  const [representationCall, representationState] = useSafeSistaAsync(
    rej.findProfileRepresentation
  );
  const loadRepresentation = useCallback(() => {
    if (canSetSubstitute) {
      representationCall({ profileId: loggedAccount.activeProfileId });
    }
  }, [canSetSubstitute, representationCall, loggedAccount.activeProfileId]);
  useEffect(loadRepresentation, [loadRepresentation]);
  const representation = canSetSubstitute
    ? representationState.value?.[0]
    : undefined;

  const [endRepresentationCall, endRepresentationState] = useSafeSistaAsync(
    useCallback(async () => {
      const yes = await confirm({
        question: representation?.pending
          ? t(
              "ucet.representation.cancelRequestConfirmation",
              "Opravdu chcete žádost o zástup zrušit?"
            )
          : t(
              "ucet.representation.endConfirmation",
              "Opravdu chcete zástup ukončit? Zástupce za vás od této chvíle nebude moci jednat a dostane o tom e-mail."
            ),
      });
      if (!yes) return;
      await rej.endRepresentation({ profileId: loggedAccount.activeProfileId });
      showSuccess(
        representation?.pending
          ? t(
              "ucet.representation.requestCanceled",
              "Žádost o zástup byla zrušena"
            )
          : t("ucet.representation.ended", "Zástup byl ukončen")
      );
      loadRepresentation();
    }, [
      confirm,
      t,
      rej,
      loggedAccount.activeProfileId,
      showSuccess,
      loadRepresentation,
      representation?.pending,
    ])
  );

  const canManageRepresentations = useCanManageRepresentations(
    loggedAccount.providerId
  );
  const manageRepresentationsAction = canManageRepresentations
    ? [
        {
          title: t("ucet.representation.adminTitle", "Zástupy zaměstnanců"),
          action: () => navigate("/zastupy"),
          primary: false,
        },
      ]
    : [];

  const substituteAction =
    canSetSubstitute &&
    representationState.value != null &&
    representation == null
      ? [
          {
            title: t("ucet.profile.setSubstitute", "Nastavit zástup"),
            action: doSetSubstitute,
            primary: false,
          },
        ]
      : [];

  const hasExpert = loggedAccount.profiles.some(
    (value) => value.type === "EXPERT"
  );

  const hasApplicant = loggedAccount.profiles.some(
    (value) => value.type === "APPLICANT"
  );

  const profiles = loggedAccount.profiles.filter((p) => p.type !== "ISTA");

  const headerConfig: DistaPageHeaderProps = {
    breadcrumbs: [
      {
        title: t("ucet.menu.breadcrumbs.home", "Domů"),
        action: () => (window.location.href = "/ucet/"),
      },
      {
        title: t("ucet.settings.profiles.title", "Uživatelské profily"),
      },
    ],
    pageTitle: t("ucet.settings.profiles.title", "Uživatelské profily"),
    chipsDefinition: [
      //{label: t('chips.approved', 'Approved'), color: 'primary'},
    ],
    actions: [
      ...substituteAction,
      ...manageRepresentationsAction,
      ...(!hasExpert
        ? [
            {
              title:
                t("ucet.profile.createNewOfType", "Založit nový profil typu") +
                " " +
                profileName("EXPERT"),
              action: () => doCreateProfile("EXPERT"),
              primary: true,
            },
            {
              title:
                t("ucet.profile.createNewOfType", "Založit nový profil typu") +
                " " +
                profileName("APPLICANT"),
              action: () => doCreateProfile("APPLICANT"),
              primary: false,
            },
          ]
        : [
            {
              title:
                t("ucet.profile.createNewOfType", "Založit nový profil typu") +
                " " +
                profileName("APPLICANT"),
              action: () => doCreateProfile("APPLICANT"),
              primary: true,
            },
          ]),
    ],
    // {
    //     title: t('mainpage.actions.primary', 'Primary action'),
    //     action: () => alert('Primary action clicked'),
    //     primary: true,
    // },
    // {title: t('mainpage.actions.action1', 'First action'), action: () => alert('First action clicked')},
    // {title: t('mainpage.actions.action2', 'Second action'), action: () => alert('Second action clicked')},
  };

  const localNavigationConfig = useLocalNavigation();

  return (
    <SistaPageLayout localNavigationConfig={localNavigationConfig}>
      <DistaContent headerConfig={headerConfig} fullWidth={true}>
        <Box margin={2}>
          <Typography variant="body1" sx={{ my: 2 }}>
            {t(
              "ucet.settings.profiles.descrition",
              "U vašeho účtu evidujeme následující uživatelské profily. Mezi těmito profile lze přepínat a zastávat tak v SISTA různé role (expert, gestor, poskytovatel, ...). S každým profilem se pojí jiná agenda a je proto možné si ke každému profilu nastavit i jiné kontaktní údaje, které se pak v rámci SISTA budou pro příslušnou agendu využívat."
            )}
          </Typography>
          {substitutionStarting && (
            <SistaDialog
              title={t("ucet.profile.setSubstitute", "Nastavit zástup")}
            >
              <Center sx={{ my: 2 }}>
                <CircularProgress />
              </Center>
              <Typography variant={"body1"} marginY={2}>
                {t(
                  "ucet.profile.substitutionStarting",
                  "Spouštím proces nastavení zástupu, za chvíli vás přesměruji na jeho první krok."
                )}
              </Typography>
            </SistaDialog>
          )}
          {representation?.pending && (
            <Alert
              severity="info"
              sx={{ my: 2 }}
              action={
                <Stack direction="row" spacing={1}>
                  {representation.processId && (
                    <Button
                      color="inherit"
                      onClick={() =>
                        navigate(
                          "/ukol/" +
                            [
                              "urn:sista:providers:" + loggedAccount.providerId,
                              "urn:sista:profiles:" +
                                loggedAccount.activeProfileId,
                              "urn:sista:processes:" + representation.processId,
                            ].join(",")
                        )
                      }
                    >
                      {t("ucet.representation.openRequest", "Otevřít")}
                    </Button>
                  )}
                  <Button
                    color="inherit"
                    disabled={endRepresentationState.pending}
                    onClick={() => endRepresentationCall()}
                  >
                    {t("ucet.representation.cancelRequest", "Zrušit žádost")}
                  </Button>
                </Stack>
              }
            >
              {pendingRepresentationInfo(t, representation)}
            </Alert>
          )}
          {representation && !representation.pending && (
            <Alert
              severity={representation.running ? "warning" : "info"}
              sx={{ my: 2 }}
              action={
                <Button
                  color="inherit"
                  disabled={endRepresentationState.pending}
                  onClick={() => endRepresentationCall()}
                >
                  {t("ucet.representation.end", "Ukončit zástup")}
                </Button>
              }
            >
              {representation.running
                ? t(
                    "ucet.representation.runningInfo",
                    "Zastupuje vás {{name}} ({{period}}).",
                    {
                      name: representation.substituteDisplayName,
                      period: formatRepresentationPeriod(representation),
                    }
                  )
                : t(
                    "ucet.representation.plannedInfo",
                    "Máte naplánovaný zástup: {{name}} ({{period}}).",
                    {
                      name: representation.substituteDisplayName,
                      period: formatRepresentationPeriod(representation),
                    }
                  )}
            </Alert>
          )}
          <Stack direction={"column"} sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {profiles.map((profile) => {
                  return (
                    <Box key={profile.id} marginBottom={2}>
                      <ProfileCard
                        profile={profile}
                        editable={true}
                        onProfileSelected={doSwitch}
                      />
                    </Box>
                  );
                })}
                {/* <Right sx={{ mt: 4 }}>
                {!hasExpert && (
                  <SmartButton
                    sx={{ ml: 2 }}
                    onClick={() => doCreateProfile("EXPERT")}
                  >
                    {t(
                      "ucet.profile.createNewOfType",
                      "Založit nový profil typu"
                    )}
                    &nbsp;
                    {profileName("EXPERT")}
                  </SmartButton>
                )}
                {!hasApplicant && (
                  <SmartButton
                    sx={{ ml: 2 }}
                    onClick={() => doCreateProfile("APPLICANT")}
                  >
                    {t(
                      "ucet.profile.createNewOfType",
                      "Založit nový profil typu"
                    )}
                    &nbsp;
                    {profileName("APPLICANT")}
                  </SmartButton>
                )}
              </Right> */}
              </Grid>
            </Grid>
          </Stack>
        </Box>
      </DistaContent>
    </SistaPageLayout>
  );
};
